import React, { useMemo, useRef, useEffect } from "react";

import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { useColorModeValue } from "@chakra-ui/react";
import {
    buildBoxPanels,
    buildCardLeftRightWallGeometry,
    buildCardDividerGeometry,
    buildCardFrontBackWallGeometry,
} from "./boxModelUtils";
import { MeshPhysicalMaterial } from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import { GTAOPass } from "three/examples/jsm/postprocessing/GTAOPass.js";

const SCENE_SCALE = 0.01;

export const BoxSchemeCanvas3D = ({ width, depth, height, thickness, dividers = 0, isCardTray = false }) => {

    const backgroundColor = useColorModeValue("#f7fafc", "#1a202c");
    const cubeColor = useColorModeValue("#4299e1", "#63b3ed");
    const gridColor = useColorModeValue("#a0aec0", "#4a5568");
    const aoSettings = useMemo(() => ({
        radius: 0.15,
        distanceExponent: 1.5,
        thickness: 1.0,
        distanceFallOff: 0.3,
        scale: 1.0,
        samples: 16,
        blendIntensity: 0.8
    }), []);
    const plasticMaterial = useMemo(() => {
        const mat = new MeshPhysicalMaterial({
            color: cubeColor,
            roughness: 0.99,
            metalness: 0.01,
            clearcoat: 0.01,
            clearcoatRoughness: 0.35,
            ior: 1.48
        });
        return mat;
    }, [cubeColor]);

    const panelData = useMemo(
        () => buildBoxPanels({ width, depth, height, thickness, dividers, isCardTray }),
        [width, depth, height, thickness, dividers, isCardTray]
    );

    const {
        scaledPanels,
        customWallGeometries,
        cameraPosition,
        orbitTarget,
        gridSize
    } = useMemo(() => {
        const { panels, safeWidth, safeDepth, safeHeight, safeThickness, wallHeight, isCardTray: isCard } = panelData;
        const scaleVec = (arr) => arr.map((v) => v * SCENE_SCALE);
        const scaled = {
            width: safeWidth * SCENE_SCALE,
            depth: safeDepth * SCENE_SCALE,
            height: safeHeight * SCENE_SCALE,
            thickness: safeThickness * SCENE_SCALE,
            wallHeight: wallHeight * SCENE_SCALE
        };
        const scaledPanels = panels.map((panel) => ({
            ...panel,
            size: scaleVec(panel.size),
            position: scaleVec(panel.position)
        }));

        let customWallGeometries = [];
        if (isCard) {
            const leftRightWallGeom = buildCardLeftRightWallGeometry(safeDepth, wallHeight, safeThickness);
            const frontBackWallGeom = buildCardFrontBackWallGeometry(safeWidth, safeHeight, safeThickness, dividers);
            customWallGeometries = [
                {
                    key: "left-wall-notch",
                    geometry: leftRightWallGeom.clone().scale(SCENE_SCALE, SCENE_SCALE, SCENE_SCALE),
                    position: [(-safeWidth / 2 + safeThickness / 2) * SCENE_SCALE, (safeThickness + wallHeight / 2) * SCENE_SCALE, 0],
                    rotation: [0, Math.PI / 2, 0]
                },
                {
                    key: "right-wall-notch",
                    geometry: leftRightWallGeom.clone().scale(SCENE_SCALE, SCENE_SCALE, SCENE_SCALE),
                    position: [(safeWidth / 2 - safeThickness / 2) * SCENE_SCALE, (safeThickness + wallHeight / 2) * SCENE_SCALE, 0],
                    rotation: [0, -Math.PI / 2, 0]
                },
                {
                    key: "front-wall-notches",
                    geometry: frontBackWallGeom.clone().scale(SCENE_SCALE, SCENE_SCALE, SCENE_SCALE),
                    position: [0, (safeThickness + wallHeight / 2) * SCENE_SCALE, (safeDepth / 2 - safeThickness / 2) * SCENE_SCALE],
                    rotation: [0, 0, 0]
                },
                {
                    key: "back-wall-notches",
                    geometry: frontBackWallGeom.clone().scale(SCENE_SCALE, SCENE_SCALE, SCENE_SCALE),
                    position: [0, (safeThickness + wallHeight / 2) * SCENE_SCALE, (-safeDepth / 2 + safeThickness / 2) * SCENE_SCALE],
                    rotation: [0, 0, 0]
                }
            ];
            if (dividers > 0) {
                const dividerGeom = buildCardDividerGeometry(safeDepth, wallHeight, safeThickness);
                customWallGeometries.push({
                    key: "card-divider",
                    geometry: dividerGeom.clone().scale(SCENE_SCALE, SCENE_SCALE, SCENE_SCALE),
                    position: [safeWidth / 3 * SCENE_SCALE, safeThickness * SCENE_SCALE * 0.9, safeDepth * SCENE_SCALE],
                    rotation: [Math.PI / 2, 0, Math.PI / 2]
                });
            }
        }

        const maxSize = Math.max(scaled.width, scaled.height, scaled.depth);
        const distance = maxSize * 2.4;
        const cameraPos = [distance, distance * Math.SQRT2, distance];
        const target = [0, scaled.wallHeight * 0.5 + scaled.thickness / 2, 0];
        const grid = Math.max(10, Math.ceil(Math.max(scaled.width, scaled.depth) * 6));
        return {
            scaledPanels,
            customWallGeometries,
            cameraPosition: cameraPos,
            orbitTarget: target,
            gridSize: grid
        };
    }, [dividers, panelData]);

    return (
        <>
            <Canvas
                style={{ width: "100vw", height: "100vh" }}
                camera={{ position: cameraPosition, fov: 45, near: 0.01, far: 1000 }}
            >
                <color attach="background" args={[backgroundColor]} />
                <hemisphereLight intensity={0.85} groundColor={gridColor} />
                <directionalLight position={[6, 8, 4]} intensity={0.9} castShadow />
                <directionalLight position={[-8, -6, -4]} intensity={0.35} />
                {scaledPanels.map((panel) => (
                    <mesh key={panel.key} position={panel.position} material={plasticMaterial} castShadow receiveShadow>
                        <boxGeometry args={panel.size} />
                    </mesh>
                ))}
                {customWallGeometries.map((wall) => (
                    <mesh
                        key={wall.key}
                        geometry={wall.geometry}
                        position={wall.position}
                        rotation={wall.rotation}
                        material={plasticMaterial}
                        castShadow
                        receiveShadow
                    >
                    </mesh>
                ))}
                <gridHelper args={[gridSize, 20, gridColor, gridColor]} />
                <OrbitControls target={orbitTarget} enablePan />
                <GTAOPost aoSettings={aoSettings} />
            </Canvas>
        </>
    );
};

const GTAOPost = ({ aoSettings }) => {
    const { gl, scene, camera, size } = useThree();
    const composerRef = useRef();
    const gtaoRef = useRef();

    useEffect(() => {
        const composer = new EffectComposer(gl);
        const renderPass = new RenderPass(scene, camera);
        const gtaoPass = new GTAOPass(scene, camera, size.width, size.height);
        gtaoPass.output = GTAOPass.OUTPUT.Default;
        if (aoSettings?.blendIntensity !== undefined) {
            gtaoPass.blendIntensity = aoSettings.blendIntensity;
        }
        if (aoSettings) {
            const {
                blendIntensity: _ignoredBlend,
                ...gtaoParams
            } = aoSettings;
            gtaoPass.updateGtaoMaterial(gtaoParams);
        }
        const outputPass = new OutputPass();
        composer.addPass(renderPass);
        composer.addPass(gtaoPass);
        composer.addPass(outputPass);
        composer.setSize(size.width, size.height);
        composerRef.current = composer;
        gtaoRef.current = gtaoPass;
        return () => {
            composer.dispose();
            gtaoPass.dispose?.();
        };
    }, [gl, scene, camera, size, aoSettings]);

    useEffect(() => {
        if (composerRef.current) {
            composerRef.current.setSize(size.width, size.height);
        }
        if (gtaoRef.current) {
            gtaoRef.current.setSize(size.width, size.height);
        }
    }, [size]);

    useEffect(() => {
        if (!gtaoRef.current || !aoSettings) return;
        const { blendIntensity: aoBlend, ...gtaoParams } = aoSettings;
        if (aoBlend !== undefined) {
            gtaoRef.current.blendIntensity = aoBlend;
        }
        gtaoRef.current.updateGtaoMaterial(gtaoParams);
    }, [aoSettings]);

    useFrame((_, delta) => {
        if (composerRef.current) {
            composerRef.current.render(delta);
        }
    }, 1);

    return null;
};
